import React, { useState, useCallback } from "react";
import { ReactReadyWrapper } from "./ReactReadyWrapper";
import { useTeamData } from "../stores/appStore";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Users,
  Plus,
  Search,
  DollarSign,
  UserCheck,
  Zap,
  Briefcase,
  Code,
  Palette,
  TrendingUp,
  Headphones,
  Settings,
  X,
} from "lucide-react";

// Preset roles with default salaries (in USD, will be converted based on currency)
const PRESET_ROLES = [
  // Founders & Leadership
  { name: "Founder/CEO", category: "founder", salary: 150000, icon: Briefcase },
  { name: "CTO", category: "engineering", salary: 180000, icon: Code },
  { name: "Product Manager", category: "operations", salary: 120000, icon: Settings },

  // Engineering
  { name: "Senior Engineer", category: "engineering", salary: 140000, icon: Code },
  { name: "Full Stack Developer", category: "engineering", salary: 110000, icon: Code },
  { name: "Frontend Developer", category: "engineering", salary: 95000, icon: Code },
  { name: "Backend Developer", category: "engineering", salary: 105000, icon: Code },
  { name: "DevOps Engineer", category: "engineering", salary: 125000, icon: Code },

  // Design
  { name: "UX/UI Designer", category: "design", salary: 85000, icon: Palette },
  { name: "Product Designer", category: "design", salary: 95000, icon: Palette },

  // Operations & Sales
  { name: "Operations Manager", category: "operations", salary: 90000, icon: Settings },
  { name: "Sales Manager", category: "sales", salary: 100000, icon: TrendingUp },
  { name: "Customer Success", category: "support", salary: 65000, icon: Headphones },

  // Support
  { name: "Support Specialist", category: "support", salary: 55000, icon: Headphones },
];

interface QuickAddDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onAddRole: (role: typeof PRESET_ROLES[0]) => void;
}

function QuickAddDialog({ isOpen, onOpenChange, onAddRole }: QuickAddDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const filteredRoles = PRESET_ROLES.filter(role => {
    const matchesSearch = role.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || role.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddRole = (role: typeof PRESET_ROLES[0]) => {
    onAddRole(role);
    onOpenChange(false);
    setSearchTerm("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Quick Add Team Member
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                <SelectItem value="founder">Founder</SelectItem>
                <SelectItem value="engineering">Engineering</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="operations">Operations</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="support">Support</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Role Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto">
            {filteredRoles.map((role) => {
              const Icon = role.icon;
              return (
                <Card
                  key={role.name}
                  className="cursor-pointer hover:shadow-md transition-all duration-200 border-2 hover:border-primary/50"
                  onClick={() => handleAddRole(role)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-medium text-sm">{role.name}</h3>
                          <p className="text-xs text-muted-foreground capitalize">{role.category}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">${role.salary.toLocaleString()}/yr</div>
                        <div className="text-xs text-muted-foreground">${Math.round(role.salary / 12).toLocaleString()}/mo</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredRoles.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No roles found matching your search.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface TeamMemberCardProps {
  role: any;
  onRemove: (id: string) => void;
  onUpdateCount: (id: string, count: number) => void;
}

function TeamMemberCard({ role, onRemove, onUpdateCount }: TeamMemberCardProps) {
  const Icon = role.icon || Users;
  const currentHeadcount = role.headcount[0] || 0; // Current month headcount

  return (
    <Card className="relative">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className="w-4 h-4 text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-sm">{role.name}</h3>
              <Badge variant="secondary" className="text-xs capitalize">
                {role.category}
              </Badge>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemove(role.id)}
            className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
          >
            <X className="w-3 h-3" />
          </Button>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Salary:</span>
            <span className="font-medium">${role.salary.toLocaleString()}/yr</span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Headcount:</span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onUpdateCount(role.id, Math.max(0, currentHeadcount - 1))}
                className="h-6 w-6 p-0"
              >
                -
              </Button>
              <span className="w-8 text-center text-sm font-medium">{currentHeadcount}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onUpdateCount(role.id, currentHeadcount + 1)}
                className="h-6 w-6 p-0"
              >
                +
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm pt-1 border-t">
            <span className="text-muted-foreground">Total Cost:</span>
            <span className="font-semibold text-primary">
              ${(role.salary * currentHeadcount).toLocaleString()}/yr
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TeamStructureBuilderInner() {
  const { teamRoles, addTeamRole, deleteTeamRole, updateHeadcount } = useTeamData();
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);

  // Calculate totals
  const totalHeadcount = teamRoles.reduce((sum, role) => sum + (role.headcount[0] || 0), 0);
  const totalSalary = teamRoles.reduce((sum, role) => sum + (role.salary * (role.headcount[0] || 0)), 0);

  const handleQuickAdd = useCallback((presetRole: typeof PRESET_ROLES[0]) => {
    // Create a new role with default headcount array (1 for current month, 0 for others)
    const headcountArray = new Array(22).fill(0);
    headcountArray[0] = 1; // Start with 1 person in the current month

    const newRole = {
      name: presetRole.name,
      salary: presetRole.salary,
      headcount: headcountArray,
      category: presetRole.category as any,
    };

    addTeamRole(newRole);
  }, [addTeamRole]);

  const handleRemoveRole = useCallback((id: string) => {
    deleteTeamRole(id);
  }, [deleteTeamRole]);

  const handleUpdateCount = useCallback((id: string, count: number) => {
    updateHeadcount(id, 0, count); // Update month 0 (current month)
  }, [updateHeadcount]);

  return (
    <div className="space-y-6">
      {/* Header with Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Team Size</p>
                <p className="text-3xl font-bold text-primary">{totalHeadcount}</p>
                <p className="text-xs text-muted-foreground">{teamRoles.length} roles</p>
              </div>
              <Users className="w-8 h-8 text-primary/60" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Monthly Burn</p>
                <p className="text-3xl font-bold text-green-600">
                  ${(Math.round(totalSalary / 12)).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">per month</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-600/60" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Annual Cost</p>
                <p className="text-3xl font-bold text-blue-600">
                  ${totalSalary.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground">total salary</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600/60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Members Grid */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Structure
              </CardTitle>
              <CardDescription>
                Build your team quickly with preset roles and easy headcount management
              </CardDescription>
            </div>
            <QuickAddDialog
              isOpen={isQuickAddOpen}
              onOpenChange={setIsQuickAddOpen}
              onAddRole={handleQuickAdd}
            />
          </div>
        </CardHeader>
        <CardContent>
          {teamRoles.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                Start Building Your Team
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Add team members quickly with our preset roles and default salaries.
              </p>
              <Button onClick={() => setIsQuickAddOpen(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Add First Team Member
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="text-sm text-muted-foreground">
                  {teamRoles.length} role{teamRoles.length !== 1 ? 's' : ''} • {totalHeadcount} team member{totalHeadcount !== 1 ? 's' : ''}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setIsQuickAddOpen(true)}
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Member
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamRoles.map((role) => (
                  <TeamMemberCard
                    key={role.id}
                    role={role}
                    onRemove={handleRemoveRole}
                    onUpdateCount={handleUpdateCount}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      {teamRoles.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
            <CardDescription>
              Common team building shortcuts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button
                variant="outline"
                onClick={() => handleQuickAdd(PRESET_ROLES.find(r => r.name === "Senior Engineer")!)}
                className="h-auto p-4 flex flex-col gap-2"
              >
                <Code className="w-6 h-6" />
                <span className="text-sm">Add Engineer</span>
              </Button>

              <Button
                variant="outline"
                onClick={() => handleQuickAdd(PRESET_ROLES.find(r => r.name === "Product Manager")!)}
                className="h-auto p-4 flex flex-col gap-2"
              >
                <Settings className="w-6 h-6" />
                <span className="text-sm">Add PM</span>
              </Button>

              <Button
                variant="outline"
                onClick={() => handleQuickAdd(PRESET_ROLES.find(r => r.name === "UX/UI Designer")!)}
                className="h-auto p-4 flex flex-col gap-2"
              >
                <Palette className="w-6 h-6" />
                <span className="text-sm">Add Designer</span>
              </Button>

              <Button
                variant="outline"
                onClick={() => handleQuickAdd(PRESET_ROLES.find(r => r.name === "Sales Manager")!)}
                className="h-auto p-4 flex flex-col gap-2"
              >
                <TrendingUp className="w-6 h-6" />
                <span className="text-sm">Add Sales</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function TeamStructureBuilder() {
  return (
    <ReactReadyWrapper>
      <TeamStructureBuilderInner />
    </ReactReadyWrapper>
  );
}